import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {EventEmitter, Inject, Injectable, inject} from '@angular/core';
import {
  BUID,
  ErrorReferenceService,
  HTTPCONSTANTS,
  MerchantService,
  NotificationService,
  OdinFeatures,
  UserProfileSettings
} from '@odin/odin-core';
import {SettingsService} from '@odin/odin-settings';
import {UserAccessLevel} from '../models/user-access-level';
import {
  MerchantChangeService,
  UserProfile,
  getActiveOrganisationId,
  getBusinessUnitsForActiveOrg,
} from '@odin/odin-core';
import {SignInSession} from '../models/SignInSession';
import {
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {catchError, map, Observable, of} from 'rxjs';

@Injectable({providedIn: 'root'})
export class CognitoService {
  public loginChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  public profileChanged: EventEmitter<UserProfile> = new EventEmitter<UserProfile>();
  private tokenRefreshTimeout = -1;
  private isLoggingIn = false;
  private refreshInFlight: Promise<boolean> | null = null;
  private errorReference = inject(ErrorReferenceService);
  constructor(
    @Inject('features') private featureHelper: OdinFeatures,
    @Inject('userPoolId') private _userPoolId: string,
    @Inject('clientId') private _clientId: string,
    @Inject('hostedLoginUrl') private hostedLoginUrl: string,
    @Inject('authCallbackUrl') private authCallbackUrl: string,
    @Inject('congitoResource') private congitoResource: string,
    @Inject('cognitoAudience') private cognitoAudience: string,
    private http: HttpClient,
    private settingsService: SettingsService,
    private merchantChangeService: MerchantChangeService,
    private merchantService: MerchantService,
    private notificationService: NotificationService,
  ) {}

  public async login(): Promise<void> {
    const codeVerifier = this.generateCodeVerifier();
    const state = this.generateState();
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', state);
    const codeChallenge = await this.computeCodeChallenge(codeVerifier);
    const scopes: string[] = [
      'email',
      'openid',
      'phone',
      'odin/admin-staff',
      'odin/merchant-staff',
      'aws.cognito.signin.user.admin',
      'odin/read-settlements',
      'odin/read-transactions',
    ];
    const params = new URLSearchParams({
      client_id: this._clientId,
      response_type: 'code',
      scope: scopes.join(' '),
      redirect_uri: this.authCallbackUrl,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    window.location.href = `${this.hostedLoginUrl}/oauth2/authorize?${params.toString()}`;
  }

  private base64urlEncode(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private generateCodeVerifier(): string {
    return this.base64urlEncode(crypto.getRandomValues(new Uint8Array(32)));
  }

  private async computeCodeChallenge(verifier: string): Promise<string> {
    const encoded = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', encoded);
    return this.base64urlEncode(new Uint8Array(digest));
  }

  private generateState(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  public logout(): void {
    const refreshToken = localStorage.getItem('refresh_token');

    localStorage.removeItem('auth');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('profile');
    localStorage.removeItem('merchant-ref');
    localStorage.removeItem('exp');
    localStorage.removeItem('current-merchant-buid');
    localStorage.removeItem('current-merchant-ref');
    localStorage.removeItem('current-merchant-mid');
    localStorage.removeItem('prod-mode-enabled');

    // guarantees cognito is erased
    const localKeys = Object.keys(localStorage);
    localKeys.forEach((key: string) => {
      if (key.toLowerCase().startsWith('cognito')) {
        localStorage.removeItem(key);
      }
    })

    if (refreshToken !== null) this.InvalidateToken(refreshToken);

    this.loginChanged.emit(false);

    window.location.href = `${this.hostedLoginUrl}/logout?client_id=${this._clientId
    }&logout_uri=${encodeURI(window.location.origin)}`;
  }

  private InvalidateToken(refreshToken: string): void {
    const body = new URLSearchParams();
    body.set('token', refreshToken)
    body.set('client_id', this._clientId)
    this.http.post(`${this.hostedLoginUrl}/oauth2/revoke`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }).subscribe(() => {
    })
  }

  public getAccessToken(): string {
    return localStorage.getItem('token') || '';
  }

  public userLoggedIn(): boolean {
    return this.getAccessToken() !== '' && this.isTokenValid();
  }

  public hasActiveSession(): boolean {
    return this.userLoggedIn() || localStorage.getItem('refresh_token') !== null;
  }

  public isAdmin(): boolean {
    // If user is a Monek Admin, then let them in!
    if (this.isMonekAdmin()) {
      return true;
    }

    const profileJSON = this.settingsService.GetSetting('profile');
    if (profileJSON == null) return false;

    const profile = JSON.parse(profileJSON.value) as UserProfile;

    // Preferred: Check grants for Organisation ID match
    if (Array.isArray(profile.grants)) {
      const hasOrganisationGrant = profile.grants.some(
        (g) => g.resource === `OUID${getActiveOrganisationId(profile)}`
      );
      if (hasOrganisationGrant) {
        return true;
      }
    }

    // Deprecated: Fallback to access level check
    // REMOVE IN FUTURE RELEASES!
    const adminPageRoles = [
      UserAccessLevel.Admin,
      UserAccessLevel.Reseller,
      UserAccessLevel.MerchantAdmin,
    ];
    return adminPageRoles.indexOf(profile.accessLevel) > -1;
  }

  public isPartner(): boolean {
    const profileJSON = this.settingsService.GetSetting('profile');
    if (profileJSON == null) return false;

    const profile = JSON.parse(profileJSON.value) as UserProfile;

    return !!profile.partnerId;
  }


  public isMonekAdmin(): boolean {
    const profileJSON = this.settingsService.GetSetting('profile');
    if (profileJSON == null) return false;

    const profile = JSON.parse(profileJSON.value) as UserProfile;

    // Preferred: Check for wildcard admin grant
    if (Array.isArray(profile.grants)) {
      const hasWildcardGrant = profile.grants.some(
        (g) => g.action === '*' && g.resource === '*'
      );
      if (hasWildcardGrant) {
        return true;
      }
    }

    // Deprecated: Fallback to access level check
    // REMOVE IN FUTURE RELEASES!
    const adminPageRoles = [UserAccessLevel.Admin];
    return adminPageRoles.includes(profile.accessLevel);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseJWT(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    return JSON.parse(jsonPayload);
  }

  public CompleteLogin(): void {
    this.loginChanged.emit(true);
  }

  // Fetch profile settings for logged in user
  public fetchProfileSettings(userId: string): Observable<UserProfileSettings> {
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/identity-management/users/${userId}/profile/settings`;
    return this.http
      .get<UserProfileSettings>(url, {
        headers: {
          DONTRETRY: 'true',
        },
      });
  }

  // Fetch profile settings for other users (admin)
  public fetchOtherUsersProfileSettings(organisationId: string,userId: string): Observable<UserProfileSettings> {
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/identity-management/manage-users/${organisationId}/${userId}/profile/settings`;
    return this.http
      .get<UserProfileSettings>(url, {
        headers: {
          DONTRETRY: 'true',
        },
      });
  }  

  public updateProfileSettings(userId: string, settings: UserProfileSettings): Observable<UserProfileSettings> {
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/identity-management/users/${userId}/profile/settings/update`;
    return this.http.post<UserProfileSettings>(url, settings);
  }

  /**
   * Re-fetches the user profile. `targetBuidId` names the BUID to activate if
   * the current one is no longer in the profile (e.g. after an organisation switch).
   */
  public fetchProfile(targetBuidId?: string): void {
    const tkn = localStorage.getItem('token');
    if (tkn == null) return; // kickout?
    const jwt = this.parseJWT(tkn) as {
      sub: string;
    };

    this.getUserProfile(jwt.sub)
      .subscribe(profile => {
        // null means getUserProfile already showed the error toast with its reference
        if (profile !== null) {
          this.settingsService.SetSetting('profile', JSON.stringify(profile));
          this.applyActiveMerchantFromProfile(profile, targetBuidId);
          this.merchantService.refreshFromProfile();
          this.loginChanged.emit(true);
          this.profileChanged.emit(profile);
        }
      });
  }

  /** Error from the most recent failed selectOrganisation, so callers can attach its reference to their own toast. */
  public lastSelectOrganisationError: unknown;

  public selectOrganisation(organisationId: string, targetBuidId?: string): Observable<boolean> {
    this.lastSelectOrganisationError = undefined;
    const tkn = localStorage.getItem('token');
    if (tkn == null) return of(false);
    const jwt = this.parseJWT(tkn) as {
      sub: string;
    };

    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/identity-management/users/${jwt.sub}/organisation/select`;
    return this.http.put(url, { organisationId }).pipe(
      map(() => {
        this.fetchProfile(targetBuidId);
        return true;
      }),
      catchError((err: unknown) => {
        this.lastSelectOrganisationError = err;
        return of(false);
      })
    );
  }

  /**
   * Re-points the active merchant when the refreshed profile no longer contains
   * it (e.g. after switching default organisation). If the active BUID is still
   * present nothing is emitted: re-emitting it would make every page reload its
   * data for a merchant that has not changed. When it is gone, `targetBuidId`
   * (if given and present) or the first BUID is activated via setActiveMid so the
   * selected mid is recomputed for the new BUID rather than carried over.
   */
  private applyActiveMerchantFromProfile(profile: UserProfile, targetBuidId?: string): void {
    const activeBUID = this.merchantService.getCurrentBuid();
    if (!activeBUID) return;

    const profileBuids = getBusinessUnitsForActiveOrg(profile);
    const buidInProfile = profileBuids.find(b => b.id === activeBUID.id);
    if (buidInProfile || profileBuids.length === 0) return;

    // activeBUID is an old one (organisation switched, or a new merchant was selected from the partner page)
    const target = (targetBuidId ? profileBuids.find(b => b.id === targetBuidId) : undefined) ?? profileBuids[0];
    this.merchantChangeService.setActiveMid(target);
  }

  public userProfile(): UserProfile | null {
    const profileSetting = this.settingsService.GetSetting('profile');
    if (profileSetting === null) return null;
    const profileJSON = profileSetting.value;
    return JSON.parse(profileJSON) as UserProfile;
  }

  public getUserAccessLevel(): UserAccessLevel | null {
    const profile = this.userProfile();
    if (profile === null) return null;
    return profile.accessLevel;
  }

  /** Error from the most recent failed getUserProfile, so callers can reuse its reference in their own toast. */
  public lastProfileError: unknown;

  public getUserProfile(userId: string, filterBuids: boolean = true): Observable<UserProfile | null> {
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/identity-management/users/${userId}/profile`;
    

    return this.http.get<UserProfile>(url, {
      headers: { DONTRETRY: 'true' },
    }).pipe(
      map((profile: UserProfile) => {
        // merchant admin grant support
        profile.grants = this.MapAdminGrants(profile);

        return profile;
      }),
      catchError((err: HttpErrorResponse) => {
        this.lastProfileError = err;
        this.errorReference.openErrorSnackbar(
          'Failed to fetch user profile. Please contact support.',
          err,
        );
        return of(null);
      })
    );
  }

    public getOtherUserProfile(organisationId: string, userId: string, filterBuids: boolean = true): Observable<UserProfile | null> {
      const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/identity-management/manage-users/${organisationId}/${userId}/profile`;
      

      return this.http.get<UserProfile>(url, {
        headers: { DONTRETRY: 'true' },
      }).pipe(
        map((profile: UserProfile) => {
          // merchant admin grant support
          profile.grants = this.MapAdminGrants(profile);

          const activeBUID = this.merchantService.getCurrentBuid();

          if (activeBUID) {
            const buidInProfile = profile.buids.find(b => b.id === activeBUID.id);

            if (buidInProfile) {
              this.merchantChangeService.setActiveMerchant(buidInProfile);
            }
            else {
              if (profile.buids.length > 0)
                this.merchantChangeService.setActiveMerchant(profile.buids[0]);
            }
          }

          return profile;
        }),
        catchError((err: HttpErrorResponse) => {
          this.lastProfileError = err;
          this.errorReference.openErrorSnackbar(
            'Failed to fetch other user profile. Please contact support.',
            err,
          );
          return of(null);
        })
      );
    }

  public MapAdminGrants(profile: UserProfile): { action: string, resource: string }[] {
    const grantsToAdd = [...profile.grants];
    const activeOrganisationId = getActiveOrganisationId(profile);
    const activeBuids = getBusinessUnitsForActiveOrg(profile);
    profile.grants.map((g: { action: string, resource: string }) => {
      if ((profile.accessLevel > 1 && g.action === '*' && g.resource === `OUID${activeOrganisationId}`)
        || profile.accessLevel === 3) { // MONEK admin gets all grants also
        // map 'merchant admin' grants for each buid in profile (from vor)
        activeBuids.forEach((buid: BUID) => {
          grantsToAdd.push(...[
            {action: 'settle:Read', resource: buid.id},
            {action: 'txn:Read', resource: buid.id},
            {action: 'user:Read', resource: buid.id},
            {action: 'user:Create', resource: buid.id},
            {action: 'txn:Receipts', resource: buid.id},
            {action: 'txn:Refund', resource: buid.id},
            {action: 'txn:Webhooks', resource: buid.id},
            {action: 'odn:Integrations', resource: buid.id},
          ]);

          if (profile.accessLevel === 3) {
            grantsToAdd.push(...[
              {action: 'txn:Create', resource: buid.id},
            ])
          }
        });
      }
      return g;
    });
    return grantsToAdd
  }

  // private getUserBuids(
  //   userId: string,
  //   callback: (buids: BUID[] | null) => void,
  // ): void {
  //   this.getUserProfile(userId, (profile: UserProfile | null) => {
  //     callback(profile == null ? null : profile.buids);
  //   });
  // }

  /**
   * Writes the session tokens to localStorage. Returns the decoded access token.
   */
  private persistSession(res: SignInSession): { sub: string; exp: number } {
    const accessToken = this.parseJWT(res.access_token) as {
      sub: string;
      exp: number;
    };

    localStorage.setItem('auth', JSON.stringify(res));
    localStorage.setItem('token', res.access_token);
    localStorage.setItem('refresh_token', res.refresh_token);
    localStorage.setItem('exp', accessToken.exp.toString());

    return accessToken;
  }

  /**
   * @param loadProfile when false, only the tokens are persisted. Used by the
   * silent refresh path — re-fetching and re-broadcasting an unchanged profile
   * tears down and rebuilds the nav and merchant picker, which the user sees as
   * the page flickering.
   */
  public associateToken(
    res: SignInSession,
    callback: (success: boolean) => void,
    loadProfile = true,
  ): void {
    if (res === null || res === undefined) return;

    const accessToken = this.persistSession(res);

    if (!loadProfile) {
      callback(true);
      return;
    }

    this.getUserProfile(accessToken.sub).subscribe(profile => {
      if (profile === null) {
        callback(false);
        this.loginChanged.emit(false);
        return;
      }

      // Persist profile so callers don't need a separate fetchProfile() call
      this.settingsService.SetSetting('profile', JSON.stringify(profile));
      // Re-seed the profile-derived streams (organisation, partner, user id) that
      // were constructed before the profile existed.
      this.merchantService.refreshFromProfile();
      this.profileChanged.emit(profile);

      const userBuids = profile.buids;

      if (userBuids.length > 0) {
        localStorage.setItem('merchant-ref', JSON.stringify(userBuids));

        const currentMid = localStorage.getItem('current-merchant-buid');
        if (currentMid == null) {
          const favouriteLocation = userBuids.find(b => b.favourite);
          const init_mid = favouriteLocation === undefined ? userBuids[0] : favouriteLocation;
          this.merchantChangeService.setActiveMerchant(init_mid);
        } else {
          const parsedMid = JSON.parse(currentMid) as BUID;
          const midInProfile =
            userBuids.filter((b) => b.id === parsedMid.id).length > 0;
          if (!midInProfile) {
            const favouriteLocation = userBuids.find(b => b.favourite);
            const init_mid = favouriteLocation === undefined ? userBuids[0] : favouriteLocation;
            this.merchantChangeService.setActiveMerchant(init_mid);
          } else {
            this.merchantChangeService.setActiveMerchant(parsedMid); // re-assign mid to ensure updated
          }
        }
      } else {
        localStorage.removeItem('current-merchant-buid');
        localStorage.setItem('merchant-ref', JSON.stringify(userBuids));
        const message = 'No locations for user.';
        this.notificationService.ErrorSmallDialog(message, new Error(message));
      }

      callback(true);
    });
  }

  public isTokenValid(): boolean {
    const tokenExp = Number.parseInt(localStorage.getItem('exp') || '0');
    const now = Date.now() / 1000; // convert to seconds
    return now < tokenExp;
  }

  public checkRefresh(): void {

    // Don't interfere with an in-progress login (auth-callback code exchange).
    if (this.isLoggingIn) return;
    if (localStorage.getItem('refresh_token') === null) return;

    const tokenExp = Number.parseInt(localStorage.getItem('exp') || '0');
    const now = Date.now() / 1000; // convert to seconds
    const timeTillExpiry = tokenExp - now;
    const threshold = 30;
    const timeTillRefresh = timeTillExpiry - threshold;

    if (this.tokenRefreshTimeout > -1) clearTimeout(this.tokenRefreshTimeout);

    if (timeTillRefresh <= 0) {
        this.refreshToken();
    } else {
        this.tokenRefreshTimeout = window.setTimeout(() => {
            this.refreshToken();
        }, timeTillRefresh * 1000);
    }
  }

  /**
   * Silently refreshes the access token. Resolves true once the new token is in
   * localStorage, false if the refresh could not be performed. Concurrent
   * callers share the single in-flight refresh.
   */
  public refreshToken(): Promise<boolean> {
    // Don't refresh while a login is in progress or another refresh is running.
    if (this.isLoggingIn) return Promise.resolve(false);
    if (this.refreshInFlight !== null) return this.refreshInFlight;
    if (this.featureHelper['new-profile-login']) return Promise.resolve(false);
    const profile = this.userProfile();
    if (profile === null) return Promise.resolve(false);
    const tokenString = localStorage.getItem('refresh_token');
    if (tokenString === null) return Promise.resolve(false);
    const token = new CognitoRefreshToken({
      RefreshToken: tokenString,
    });

    const user = this.getCognitoUser();
    if (user === null) return Promise.resolve(false);

    this.refreshInFlight = new Promise<boolean>((resolve) => {
      user.refreshSession(token, (err: string, session: CognitoUserSession) => {
        if (err) {
          resolve(false);
          this.logout();
          return;
        }
        // The refresh path deliberately skips the profile fetch — the tokens are
        // all that changed.
        this.associateToken(
          new SignInSession().FromCognito(session),
          (success: boolean) => {
            if (!success) {
              this.loginChanged.emit(false);
            }
            if (success) {
              this.checkRefresh();
            }
            resolve(success);
          },
          false,
        );
      });
    }).finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  private getCognitoUser(): CognitoUser | null {
    const userProfile = this.userProfile();
    if (userProfile === null) return null;
    const poolData = {
      UserPoolId: this._userPoolId,
      ClientId: this._clientId,
    };
    const userPool = new CognitoUserPool(poolData);
    const userData = {
      Username: userProfile.email,
      Pool: userPool,
    };
    return new CognitoUser(userData);
  }

  public getTokenFromCode(
    code: string,
    callback: (success: boolean) => void,
  ): void {
    // Mark login as in-progress so checkRefresh()/refreshToken() don't race
    // the code exchange and log the user out with a stale refresh token.
    this.isLoggingIn = true;
    const done = (success: boolean) => {
      this.isLoggingIn = false;
      callback(success);
    };

    const url = `${this.hostedLoginUrl}/token`;

    const lastCode = localStorage.getItem('last_code');
    if (lastCode == code) {
      done(true);
      return;
    } // dont retry this code
    localStorage.setItem('last_code', code);

    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    sessionStorage.removeItem('pkce_code_verifier');
    if (!codeVerifier) {
      const message = 'There was an error logging in. (ODN-1001)';
      this.notificationService.ErrorSmallDialog(message, new Error(message));
      done(false);
      this.loginChanged.emit(false);
      return;
    }

    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('code', code);
    body.set('redirect_uri', this.authCallbackUrl);
    body.set('resource', this.congitoResource);
    body.set('audience', this.cognitoAudience);
    body.set('client_id', this._clientId);
    body.set('code_verifier', codeVerifier);

    this.http
      .post<SignInSession>(url, body.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .subscribe(
        (res: SignInSession) => {
          this.associateToken(res, done);
        },
        (err: HttpErrorResponse) => {
          localStorage.removeItem('organisationId');
          localStorage.removeItem('auth');
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('current-merchant-buid');
          this.notificationService.ErrorSmallDialog(
            'There was an error logging in. (ODN-1000)',
            err,
          );
          done(false);
          this.loginChanged.emit(false);
        },
      );
  }
}
